import React from "react";
import { Activity, Zap } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import Button from "../Button";
import Card from "../Card/Card";
import { EVENT_NAMES, SCREEN_NAMES } from "src/helpers/analytics/events";
import analytics from "src/helpers/analytics";

interface QuickActionsCardProps {
  id: string;
  title: string;
  description: string;
  supportProgramKey: string;
  services: Array<{
    id: string;
    title?: string;
    description?: string;
  }>;
}

const QuickActionsCard: React.FC<QuickActionsCardProps> = ({
  id,
  title,
  description,
  supportProgramKey,
  services,
}) => {
  const navigate = useNavigate();
  const params = useParams();
  const screenName = params["screen-name"];

  const handleTakeAssessment = () => {
    // Event: TAKE_ASSESSMENT_CLICKED
    analytics.trackEvent(EVENT_NAMES.TAKE_ASSESSMENT_CLICKED, {
      screen_name: SCREEN_NAMES.HOME_SCREEN,
      support_program_key: supportProgramKey,
      card_title: title,
    });
    navigate(
      `/app/${supportProgramKey}/health-assistant-screen?loggerType=health assessment`
    );
  };

  const serviceDefinitions: Record<
    string,
    {
      icon: React.ReactNode;
      defaultTitle: string;
      defaultDescription: string;
      ctaText: string;
      onClick: () => void;
    }
  > = {
    "take-assessment": {
      icon: <Activity className="w-4 h-4 text-secondary" />,
      defaultTitle: "Check Quality of Life",
      defaultDescription: "AMD risk assessment",
      ctaText: "Start",
      onClick: handleTakeAssessment,
    },
  };

  return (
    <div className="mx-4" id={id}>
      <Card
        title={title}
        description={description}
        icon={<Zap className="w-4 h-4" />}
      >
        <div className="flex flex-col">
          {(services ?? []).map((service, idx) => {
            const def = serviceDefinitions[service.id];
            if (!def) return null;

            return (
              <div
                key={`${service.id}-${idx}`}
                className="flex items-center justify-between cursor-pointer hover:bg-gray-50 rounded-lg transition-colors p-2"
              >
                <div className="flex items-center gap-2">
                  <div className="text-[var(--app-primary)] bg-[var(--icon-bg-primary)] rounded-full p-2">
                    {def.icon}
                  </div>
                  <div>
                    <div className="text-gray-700 text-sm font-medium">
                      {service.title || def.defaultTitle}
                    </div>
                    <div className="text-xs text-gray-500 font-thin">
                      {service.description || def.defaultDescription}
                    </div>
                  </div>
                </div>
                <Button size="sm" rounded="full" onClick={def.onClick}>
                  {def.ctaText}
                </Button>
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
};

export default QuickActionsCard;
